import { dataService } from '../apis/dataService';
import type { ClientOverview, FinancialDataQueryParams } from '../../types/api';

/**
 * Fetch client overview data for all units of a specific client
 * @param user_id_no - The user ID number
 * @param clientUnits - Array of client units
 * @returns Promise<ClientOverview[]> - Array of client overview data for all units
 */
export async function fetchClientOverviewData(
  user_id_no: number, 
  clientUnits: Array<{ unit_id_no?: number }>
): Promise<ClientOverview[]> {
  if (!clientUnits || clientUnits.length === 0) {
    return [];
  }

  try {
    // Fetch overview data for each unit
    const overviewPromises = clientUnits.map(async (unit) => {
      if (!unit.unit_id_no) {
        return null;
      }

      const params: FinancialDataQueryParams = {
        user_id_no,
        unit_id_no: unit.unit_id_no
      };

      try {
        const overviewData = await dataService.getClientOverview(params);
        return overviewData;
      } catch (error) {
        console.warn(`Failed to fetch overview for unit ${unit.unit_id_no}:`, error);
        return null;
      }
    });

    // Wait for all requests to complete
    const results = await Promise.all(overviewPromises);
    
    // Flatten and filter out null results
    return results
      .filter((result): result is ClientOverview[] => result !== null)
      .flat();
  } catch (error) {
    console.error('Error fetching client overview data:', error);
    throw error;
  }
}

/**
 * Calculate aggregated metrics for a client across all units
 * @param clientOverviewData - Array of ClientOverview data for all units
 * @returns Object with aggregated metrics
 */
export function calculateClientAggregatedMetrics(clientOverviewData: ClientOverview[]) {
  if (!clientOverviewData || clientOverviewData.length === 0) {
    return {
      totalAssetsUSD: 0,
      totalAssetsMarketValueUSD: 0,
      totalRetainedEarningsUSD: 0,
      averageROI: 0,
      totalNetAnnualRentYieldUSD: 0,
      unitCount: 0
    };
  }

  const totals = clientOverviewData.reduce((acc, overview) => {
    acc.totalAssetsUSD += overview.total_assets_usd || 0;
    acc.totalAssetsMarketValueUSD += overview.assets_market_value_usd || 0;
    acc.totalRetainedEarningsUSD += overview.retained_earnings_usd || 0;
    acc.totalNetAnnualRentYieldUSD += overview.net_annual_rent_yield_usd || 0;
    acc.roiSum += overview.ttm_roi_percent || 0;
    acc.unitCount += 1;
    return acc;
  }, {
    totalAssetsUSD: 0,
    totalAssetsMarketValueUSD: 0,
    totalRetainedEarningsUSD: 0,
    totalNetAnnualRentYieldUSD: 0,
    roiSum: 0,
    unitCount: 0
  });

  return {
    totalAssetsUSD: totals.totalAssetsUSD,
    totalAssetsMarketValueUSD: totals.totalAssetsMarketValueUSD,
    totalRetainedEarningsUSD: totals.totalRetainedEarningsUSD,
    totalNetAnnualRentYieldUSD: totals.totalNetAnnualRentYieldUSD,
    averageROI: totals.unitCount > 0 ? totals.roiSum / totals.unitCount : 0,
    unitCount: totals.unitCount
  };
}












